import { useEffect, useMemo, useState } from "react";
import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";
import { toast } from "react-toastify";

const useSharedGroupData = ({
    view,
    navState,
    token,
    userDetails,
    schoolYearDto,
    currentTimePeriodDto,
    incomingTemplateId,
    overrideTeacherGuid,
    studentNumber
}) => {
    const [allSharedGroups, setAllSharedGroups] = useState(null);
    const [sharedGroupGradingProfileDto, setSharedGroupGradingProfileDto] = useState(null);

    const effectiveGuid = overrideTeacherGuid || userDetails?.uid;

    const sharedGroup = useMemo(() => {
        if (navState?.sharedGroupId && Array.isArray(allSharedGroups)) {
            return allSharedGroups.find((g) => String(g.sharedGroupId) === String(navState.sharedGroupId));
        }

        if (studentNumber && Array.isArray(allSharedGroups)) {
            return allSharedGroups.find((group) =>
                group.sharedGroupStudents?.some((s) => String(s.studentNumber) === String(studentNumber))
            );
        }

        return null;
    }, [allSharedGroups, navState?.sharedGroupId, studentNumber]);

    // Get the selected grade for groups view
    const selectedGradeForGroups = useMemo(() => {
        if (view !== "GROUPS") return null;
        return sharedGroup?.sharedGradeName ?? null;
    }, [view, sharedGroup?.sharedGradeName]);

    // Combined grades array
    const combinedGrades = useMemo(
        () => (selectedGradeForGroups ? [selectedGradeForGroups] : []),
        [selectedGradeForGroups]
    );

    // Calculate effective grading criteria ID for groups
    const effectiveGradingCriteriaId = useMemo(() => {
        if (view !== "GROUPS") return null;
        if (!incomingTemplateId || incomingTemplateId === "SLE") return null;

        const list = sharedGroupGradingProfileDto?.gradingCriteriaList ?? [];
        const match = list.find((c) => String(c.parentTemplate) === String(incomingTemplateId));
        return match?.key ?? null;
    }, [view, incomingTemplateId, sharedGroupGradingProfileDto]);

    // Calculate shared group permission
    const sharedGroupPermission = useMemo(() => {
        if (!sharedGroup?.groupShareDto?.gradingCriteriaShares || !effectiveGradingCriteriaId) return "VIEW";
        const shares = sharedGroup.groupShareDto.gradingCriteriaShares.filter(
            (s) => String(s.gradingCriteriaId) === String(effectiveGradingCriteriaId)
        );
        return shares.some((s) => s.permission === "EDIT") ? "EDIT" : "VIEW";
    }, [sharedGroup, effectiveGradingCriteriaId]);

    // Fetch all shared groups
    useEffect(() => {
        if (view !== "GROUPS") return;
        if (!token || !effectiveGuid) return;
        if (!schoolYearDto?.key || !currentTimePeriodDto?.key) return;

        ElementaryProgressReportDao({
            action: "getAllSharedGroups",
            params: {
                teacherGuid: effectiveGuid,
                schoolYearKey: schoolYearDto.key,
                timePeriod: currentTimePeriodDto.key
            },
            token
        })
            .then((resp) => setAllSharedGroups(resp?.data?.payload ?? []))
            .catch((err) => {
                console.error("Error fetching shared groups:", err);
                toast.error("There was a problem loading shared groups information. Please try again later.");
                setAllSharedGroups([]);
            });
    }, [view, token, effectiveGuid, schoolYearDto?.key, currentTimePeriodDto?.key]);

    // Fetch shared group grading profile
    useEffect(() => {
        if (view !== "GROUPS") return;
        if (!token) return;
        if (!sharedGroup?.sharedGradingProfile) return;

        ElementaryProgressReportDao({
            action: "getGradingProfileById",
            gradingProfileId: sharedGroup.sharedGradingProfile,
            token
        })
            .then((resp) => setSharedGroupGradingProfileDto(resp?.data?.payload ?? null))
            .catch((err) => {
                console.error("Error fetching shared group grading profile:", err);
                toast.error("There was a problem loading shared groups information. Please try again later.");
                setSharedGroupGradingProfileDto(null);
            });
    }, [view, token, sharedGroup?.sharedGradingProfile]);

    return {
        allSharedGroups,
        sharedGroup,
        selectedGradeForGroups,
        combinedGrades,
        sharedGroupGradingProfileDto,
        sharedGroupPermission
    };
};

export default useSharedGroupData;
